import api from './api';
import type { MenuItemFormat, MenuVisibility } from '../types/menu';

/**
 * Per-item format pricing. Mounted at /admin/venue/:venueId/menu-item/:itemId/formats
 * (parallel to /menu/...). itemFormatId is the join row id.
 */
const base = (venueId: string, itemId: string) =>
  `/admin/venue/${venueId}/menu-item/${itemId}/formats`;

const menuItemFormatService = {
  /** List format pricing rows for an item, with the format catalog populated. */
  getItemFormats: async (venueId: string, itemId: string): Promise<MenuItemFormat[]> => {
    const response = await api.get(base(venueId, itemId));
    return response.data;
  },

  /**
   * Create a format-pricing row. inventoryItemId is optional and accepted as a
   * bare ObjectId — storage-only, no existence check.
   */
  createItemFormat: async (
    venueId: string,
    itemId: string,
    data: {
      formatId: string;
      price: number;
      visibility?: MenuVisibility;
      inventoryItemId?: string | null;
    },
  ): Promise<MenuItemFormat> => {
    const response = await api.post(base(venueId, itemId), data);
    return response.data;
  },

  /** Partial update. formatId is immutable; create a new row to point elsewhere. */
  updateItemFormat: async (
    venueId: string,
    itemId: string,
    itemFormatId: string,
    data: Partial<{
      price: number;
      visibility: MenuVisibility;
      inventoryItemId: string | null;
    }>,
  ): Promise<MenuItemFormat> => {
    const response = await api.patch(`${base(venueId, itemId)}/${itemFormatId}`, data);
    return response.data;
  },

  /** Hard delete a row. Use visibility=HIDDEN to keep the price but hide it. */
  deleteItemFormat: async (venueId: string, itemId: string, itemFormatId: string): Promise<void> => {
    await api.delete(`${base(venueId, itemId)}/${itemFormatId}`);
  },
};

export default menuItemFormatService;
